import type { ReportAggregation } from "@glorify-llc/accounting-core";
import { safeParseDateString } from "@/lib/date-utils";

/**
 * Stable, lowercase status enum the adapter consumes. The data layer stores
 * `volunteer_assignments.status` as a free-form string; we normalize on entry
 * (see {@link bucketVolunteerAssignmentRow}) so downstream comparisons never
 * have to second-guess casing or whitespace (#1547, #1555, #1559, #1568).
 */
export type VolunteerAssignmentStatus =
  | "confirmed"
  | "pending"
  | "declined"
  | "unknown";

export interface VolunteerAssignment {
  id: string;
  personId: string;
  volunteerName: string;
  ministry: string;
  team: string;
  role: string;
  serviceDate: Date;
  serviceName: string;
  status: VolunteerAssignmentStatus;
}

export type VolunteerScheduleReportContext = {
  asOf: Date;
  assignments: VolunteerAssignment[];
};

// ---------------------------------------------------------------------------
// Raw-row → VolunteerAssignment helper (mirrors PR #1540 ar-aging,
// #1548 bank-recon, #1559 group-roster).
// ---------------------------------------------------------------------------
//
// The `volunteer_assignments` query in `useReportData.ts` returns raw rows
// from PostgREST: snake_case fields and embedded `people`/`service_positions`/
// `services` records. The adapter expects the camelCase `VolunteerAssignment`
// shape with derived display fields (`volunteerName`, `serviceName`).
// Feeding raw rows directly to the adapter produces:
//   - personId/volunteerName → undefined → blank labels and `/volunteers?person=undefined`
//   - serviceDate → undefined → "Invalid Date" in the table
//   - status comparisons against "confirmed"/"pending" silently fail
//     (#1559 wiring class). Empty summary, empty rows.
//
// This helper centralizes the mapping so every wiring path is consistent.

export interface RawVolunteerAssignmentRow {
  id: string;
  status: string | null;
  created_at?: string | null;
  confirmed_at?: string | null;
  person_id: string | null;
  position_id?: string | null;
  service_id?: string | null;
  people?: {
    id?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  } | null;
  /**
   * `service_positions` row joined via `position_id`. Holds the role title
   * ("Greeter", "Usher") and the position type enum.
   */
  service_positions?: {
    id?: string | null;
    title?: string | null;
    position_type?: string | null;
  } | null;
  /**
   * `services` row joined via `service_id`. Holds the service date, start
   * time, and human-readable title.
   */
  services?: {
    id?: string | null;
    title?: string | null;
    scheduled_date?: string | null;
    start_time?: string | null;
    service_type?: string | null;
  } | null;
  /**
   * `volunteer_teams` is NOT directly joinable from `volunteer_assignments`
   * (no FK), but we accept the field defensively in case the wiring later
   * adds it via a derived view or a positional join.
   */
  volunteer_teams?: {
    id?: string | null;
    name?: string | null;
  } | null;
}

const KNOWN_STATUSES: ReadonlySet<VolunteerAssignmentStatus> = new Set([
  "confirmed",
  "pending",
  "declined",
]);

/**
 * Trim + lowercase + collapse to the typed enum. Anything else (including
 * null/garbage) becomes `"unknown"` so it is countable but never confused
 * with a real status.
 */
export function normalizeVolunteerStatus(
  raw: unknown,
): VolunteerAssignmentStatus {
  if (typeof raw !== "string") return "unknown";
  const trimmed = raw.trim().toLowerCase();
  if (KNOWN_STATUSES.has(trimmed as VolunteerAssignmentStatus)) {
    return trimmed as VolunteerAssignmentStatus;
  }
  return "unknown";
}

function buildVolunteerName(
  people: RawVolunteerAssignmentRow["people"],
): string {
  if (!people) return "Unknown Volunteer";
  const first = (people.first_name ?? "").trim();
  const last = (people.last_name ?? "").trim();
  const full = `${first} ${last}`.trim();
  return full.length > 0 ? full : "Unknown Volunteer";
}

function pickServiceName(services: RawVolunteerAssignmentRow["services"]): string {
  if (!services) return "Unknown Service";
  const title = typeof services.title === "string" ? services.title.trim() : "";
  if (title.length > 0) return title;
  const fallback =
    typeof services.service_type === "string" ? services.service_type.trim() : "";
  return fallback.length > 0 ? fallback : "Unknown Service";
}

/**
 * Resolve the effective service date. Prefer `services.scheduled_date`
 * (a DATE column, parsed as midnight UTC by `new Date(...)` for ISO strings).
 * Fall back to `services.start_time` (a TIMESTAMP) so a row whose date column
 * is null still places on the timeline. If both are missing/garbage, emit an
 * invalid Date — the adapter sorts those last rather than dropping the row.
 */
function pickServiceDate(
  services: RawVolunteerAssignmentRow["services"],
): Date {
  const tryParse = (value: string | null | undefined): Date | null => {
    if (typeof value !== "string" || value.trim().length === 0) return null;
    // scheduled_date is date-only: read it as that calendar day, not UTC midnight (which the
    // report runner formats as the evening before in the US). Timestamps still read as instants.
    return safeParseDateString(value);
  };
  return (
    tryParse(services?.scheduled_date) ??
    tryParse(services?.start_time) ??
    new Date(NaN)
  );
}

/**
 * Convert a raw `volunteer_assignments` row into the typed shape the adapter
 * expects. Returns `null` for rows missing essential identity (id) so they
 * don't silently render with broken drilldown URLs.
 */
export function bucketVolunteerAssignmentRow(
  raw: RawVolunteerAssignmentRow,
): VolunteerAssignment | null {
  if (!raw || !raw.id) return null;

  const personId = raw.people?.id || raw.person_id || "";
  const role =
    typeof raw.service_positions?.title === "string"
      ? raw.service_positions.title.trim()
      : "";
  const team =
    typeof raw.volunteer_teams?.name === "string"
      ? raw.volunteer_teams.name.trim()
      : "";
  const ministry =
    typeof raw.service_positions?.position_type === "string"
      ? raw.service_positions.position_type.trim()
      : "";

  return {
    id: raw.id,
    personId,
    volunteerName: buildVolunteerName(raw.people),
    ministry: ministry.length > 0 ? ministry : "Unknown Ministry",
    team: team.length > 0 ? team : "—",
    role: role.length > 0 ? role : "Unknown Role",
    serviceDate: pickServiceDate(raw.services),
    serviceName: pickServiceName(raw.services),
    status: normalizeVolunteerStatus(raw.status),
  };
}

// ---------------------------------------------------------------------------

export function buildVolunteerScheduleReportAggregation(
  context: VolunteerScheduleReportContext,
): ReportAggregation {
  const assignments = Array.isArray(context.assignments)
    ? context.assignments
    : [];

  // Single-pass tally — keeps counts perfectly aligned with the rows we emit
  // and avoids three separate `.filter().length` walks. Status is normalized
  // (case + whitespace) so legacy data with " Confirmed " or "DECLINED" is
  // counted correctly (#1547, #1555 case-sensitivity class).
  let confirmed = 0;
  let pending = 0;
  let declined = 0;
  for (const a of assignments) {
    const status = normalizeVolunteerStatus(a?.status);
    if (status === "confirmed") confirmed += 1;
    else if (status === "pending") pending += 1;
    else if (status === "declined") declined += 1;
  }

  const total = assignments.length;
  // Divide-by-zero / NaN guard: empty schedule must yield 0%, not NaN%.
  const fillRateRaw = total > 0 ? (confirmed / total) * 100 : 0;
  const fillRate = Number.isFinite(fillRateRaw) ? fillRateRaw : 0;

  const rows = assignments
    .map((assignment) => {
      const personId =
        typeof assignment.personId === "string" ? assignment.personId : "";
      const volunteerName =
        typeof assignment.volunteerName === "string" &&
        assignment.volunteerName.trim().length > 0
          ? assignment.volunteerName
          : "Unknown Volunteer";
      const serviceName =
        typeof assignment.serviceName === "string" &&
        assignment.serviceName.trim().length > 0
          ? assignment.serviceName
          : "Unknown Service";
      // Drop trailing slash dragging an empty id (#1559 pattern).
      const drilldownUrl = personId
        ? `/volunteers?person=${encodeURIComponent(personId)}`
        : `/volunteers`;
      // Defensive: assignment.serviceDate may be a Date, an ISO string from
      // a stale fixture, or null/undefined. Coerce to a Date and let the
      // sort comparator handle invalid Dates.
      const serviceDate =
        assignment.serviceDate instanceof Date
          ? assignment.serviceDate
          : assignment.serviceDate
            ? new Date(assignment.serviceDate as unknown as string)
            : new Date(NaN);
      return {
        id: assignment.id,
        label: `${volunteerName} - ${serviceName}`,
        entityType: "person" as const,
        drilldownUrl,
        volunteerName,
        ministry:
          typeof assignment.ministry === "string" && assignment.ministry.trim().length > 0
            ? assignment.ministry
            : "Unknown Ministry",
        team:
          typeof assignment.team === "string" && assignment.team.trim().length > 0
            ? assignment.team
            : "—",
        role:
          typeof assignment.role === "string" && assignment.role.trim().length > 0
            ? assignment.role
            : "Unknown Role",
        serviceDate,
        serviceName,
        status: normalizeVolunteerStatus(assignment.status),
      };
    })
    // Stable, locale-independent sort:
    //   1. Earliest valid serviceDate first.
    //   2. Invalid/NaN dates sorted last (so they don't poison the timeline).
    //   3. Ties → volunteerName (locale-independent `<`/`>`).
    //   4. Final tiebreak → id, for full determinism across refetches.
    .sort((a, b) => {
      const aTime = a.serviceDate instanceof Date ? a.serviceDate.getTime() : NaN;
      const bTime = b.serviceDate instanceof Date ? b.serviceDate.getTime() : NaN;
      const aValid = Number.isFinite(aTime);
      const bValid = Number.isFinite(bTime);
      if (aValid && !bValid) return -1;
      if (!aValid && bValid) return 1;
      if (aValid && bValid && aTime !== bTime) return aTime - bTime;
      if (a.volunteerName < b.volunteerName) return -1;
      if (a.volunteerName > b.volunteerName) return 1;
      if (a.id < b.id) return -1;
      if (a.id > b.id) return 1;
      return 0;
    });

  return {
    asOf: context.asOf,
    summary: [
      { label: "Total Assignments", value: total },
      { label: "Confirmed", value: confirmed },
      { label: "Pending", value: pending },
      { label: "Declined", value: declined },
      // Two-decimal precision matches the existing public contract; the
      // legacy `Math.round(x * 100) / 100` form is preserved but now fed
      // a guarded numerator so NaN never reaches the report (#1).
      { label: "Fill Rate %", value: Math.round(fillRate * 100) / 100 },
    ],
    rows,
  };
}
